// Postęp prac liczony ze stanu budowy: co jest do zrobienia w pomieszczeniu, co zrobione, czy obwód jest gotowy do pomiaru.
import { TYPY_PUNKTOW, PRACE_POMIESZCZENIA, PRACE_ODCINKA } from './katalog.js';
import { zrobione } from './zdarzenia.js';

export const praceTypu = (typ, etap) => (TYPY_PUNKTOW[typ]?.prace || []).filter((p) => p.etap === etap);

// Indeks punktów i odcinków tras według pomieszczenia i obwodu (wydajność 14.10: duża budowa z 300 pomieszczeniami i 3000 punktów
// liczyła listę pięter 0,6 s, bo każde pomieszczenie przeglądało wszystkie punkty). Indeks liczy się raz na stan budowy:
// kluczem jest obiekt S.punkty / S.trasy, który powstaje od nowa w każdym stan() i nie jest potem zmieniany.
const IDX = new WeakMap();
const grupuj = (L, klucz) => { const M = new Map(); for (const x of L) { const k = klucz(x); if (k == null) continue; let a = M.get(k); if (!a) M.set(k, (a = [])); a.push(x); } return M; };
function indeks(S) {
  let I = IDX.get(S.punkty); if (I && I.trasy === S.trasy) return I;
  const P = Object.values(S.punkty || {}).filter((p) => !p.usuniety);
  I = { trasy: S.trasy, pom: grupuj(P, (p) => p.pomieszczenieId), obw: grupuj(P, (p) => p.obwodId),
    odc: grupuj(Object.values(S.trasy || {}).flatMap((t) => t.odcinki.map((o) => ({ o, t }))), (x) => x.o.pomieszczenieId) };
  IDX.set(S.punkty, I); return I;
}
/** Punkty pomieszczenia (bez usuniętych). */
export const punktyPomieszczenia = (S, pomId) => indeks(S).pom.get(pomId) || [];
/** Odcinki tras w pomieszczeniu: [{ o: odcinek, t: trasa }]. */
export const odcinkiPomieszczenia = (S, pomId) => indeks(S).odc.get(pomId) || [];

/** Wszystkie pozycje do odhaczenia w pomieszczeniu dla etapu B albo W: [{obiektId, praca, grupa}] */
export function pozycje(S, pomId, etap) {
  const out = [];
  // pomieszczenie pomocnicze („bez nr” z kreatora planu) to nie prawdziwe pomieszczenie: bez zdjęć ścian
  if (etap === 'B' && !S.pomieszczenia[pomId]?.pomocnicze) for (const p of PRACE_POMIESZCZENIA) out.push({ obiektId: pomId, praca: p, grupa: 'pomieszczenie' });
  for (const pkt of punktyPomieszczenia(S, pomId))
    for (const p of praceTypu(pkt.typ, etap)) out.push({ obiektId: pkt.id, praca: p, grupa: pkt.obwodId || (pkt.zPlanu?.port || /^data/.test(pkt.typ) ? 'dane' : 'bez obwodu'), punkt: pkt });
  if (etap === 'B') for (const { o, t } of odcinkiPomieszczenia(S, pomId))
    for (const p of PRACE_ODCINKA) out.push({ obiektId: o.id, praca: p, grupa: 'trasy', odcinek: o, trasa: t });
  return out;
}

export function liczPostep(S, pomId, etap) {
  const P = pozycje(S, pomId, etap);
  const zr = P.filter((x) => zrobione(S, x.obiektId, x.praca.id)).length;
  return { zrobione: zr, wszystkie: P.length, gotowe: P.length > 0 && zr === P.length };
}

/** Obwód gotowy do pomiaru: wszystkie jego punkty (we wszystkich pomieszczeniach) mają skończony brudny i biały montaż. */
export function obwodGotowy(S, obwodId) {
  const pkt = indeks(S).obw.get(obwodId) || [];
  const brak = pkt.filter((p) => ['B', 'W'].some((e) => praceTypu(p.typ, e).some((pr) => !zrobione(S, p.id, pr.id))));
  return { gotowy: pkt.length > 0 && !brak.length, punktow: pkt.length, brakuje: brak.length };
}

export const obwodyPomieszczenia = (S, pomId) => [...new Set(punktyPomieszczenia(S, pomId).filter((p) => p.obwodId).map((p) => p.obwodId))].sort();

/** Stan pomieszczenia do rzutu: brudny, biały, pomiary (wszystkie obwody pomieszczenia zmierzone z wynikiem OK). */
export function stanPomieszczenia(S, pomId) {
  const B = liczPostep(S, pomId, 'B'), W = liczPostep(S, pomId, 'W');
  const obw = obwodyPomieszczenia(S, pomId);
  const zm = obw.filter((o) => S.pomiary?.[o]?.wynik === 'ok').length;
  // pomieszczenie bez obwodów 230 V (np. same gniazda danych z portami): nie ma czego mierzyć, pomiary są gotowe razem z białym montażem
  return { B, W, P: { zrobione: zm, wszystkie: obw.length, gotowe: obw.length > 0 ? zm === obw.length : W.gotowe } };
}

// „Co jeszcze” (W26): filtr listy pomieszczeń i punktów na rzucie. Każdy filtr zwraca, ile zostało w pomieszczeniu i których punktów to dotyczy.
export const FILTRY = {
  B: 'brudny montaż niegotowy', W: 'biały montaż niegotowy', P: 'obwody bez pomiaru', spr: 'punkty do sprawdzenia', blokada: 'aktywna blokada', usterka: 'otwarta usterka',
};
const brakEtapu = (S, p, e) => praceTypu(p.typ, e).some((pr) => !zrobione(S, p.id, pr.id));

/** { n, punkty: [id] } dla pomieszczenia; n = liczba rzeczy do zrobienia w tym filtrze (punkty, obwody, blokady albo usterki). */
export function coJeszcze(S, pomId, filtr) {
  const pkt = punktyPomieszczenia(S, pomId);
  if (filtr === 'B' || filtr === 'W') { const L = pkt.filter((p) => brakEtapu(S, p, filtr)).map((p) => p.id);
    // zdjęcia ścian pomieszczenia (etap B) też są pracą do zrobienia, choć nie są punktem
    const sciany = filtr === 'B' && pozycje(S, pomId, 'B').some((x) => x.grupa === 'pomieszczenie' && !zrobione(S, x.obiektId, x.praca.id)) ? 1 : 0;
    return { n: L.length + sciany, punkty: L, sciany: !!sciany }; }
  if (filtr === 'P') { const obw = obwodyPomieszczenia(S, pomId).filter((o) => S.pomiary?.[o]?.wynik !== 'ok');
    return { n: obw.length, punkty: pkt.filter((p) => obw.includes(p.obwodId)).map((p) => p.id) }; }
  if (filtr === 'spr') { const L = pkt.filter((p) => p.doSprawdzenia).map((p) => p.id); return { n: L.length, punkty: L }; }
  if (filtr === 'blokada') return { n: Object.values(S.blokady || {}).filter((b) => b.pomieszczenieId === pomId && b.aktywna).length, punkty: [] };
  if (filtr === 'usterka') { const U = Object.values(S.usterki || {}).filter((u) => u.pomieszczenieId === pomId && u.otwarta);
    return { n: U.length, punkty: U.map((u) => u.punktId).filter(Boolean) }; }
  return { n: 0, punkty: [] };
}
